import { useEffect, useRef } from 'react'

import { API_BASE } from '../../app/api'
import { openRevivingEventSource } from '../../app/sse-revive'
import type { ActivityQuery } from '../../app/activity-query'
import { activityStreamParams } from './filter'
import type { ActivityItem } from './merge'

/**
 * 动态中心自己的 SSE。
 *
 * 断线立刻回调 `onDisconnect`；网络层断掉那一种由浏览器自己带
 * `Last-Event-ID` 接回来，**我们一根都不多开**——插手就是同一条流上
 * 两根连接、两份游标。
 *
 * # 另一半断法浏览器不管
 *
 * 拿到了响应但它不对（status ≠ 200、Content-Type 不对）时，规范要求
 * **fail the connection**：readyState 落 CLOSED，`error` 只发一次，
 * 从此不再重试。那正是控制面重启那几秒的 502。不接的话这条流就永久死了，
 * 而屏幕上只会挂着一行「连接断开」，别的请求照样 200。
 * 判断与退避在 `app/sse-revive.ts`，与 Lulu 会话流、全站 `/events` 同一份。
 *
 * # 自己接回来的那一根，位置是丢了的——所以要说出来
 *
 * 新开的 `EventSource` 带不上 `Last-Event-ID`（那是浏览器在同一根上续传
 * 才有的头），所以这里记住最后的 SSE id，并在自建连接时用 `?cursor=`
 * 交还服务端。服务端仍可能因保留窗口或水位变化发 `stream.reset`，
 * 那时再走列表整份对齐。
 *
 * 所以自己接回来的那一根**真的连上之后**要调一次 `onReset`：那正是这一屏
 * 为「我丢了位置」准备好的路（`index.tsx` 收到它会重新拉列表）。
 * **不调的话，屏幕会安安静静地少掉一段历史，而且没有任何东西会红**——
 * 这个仓库把那种失败叫「用例全绿线上坏」。
 *
 * # 「真的连上之后」那半句不是措辞，是这条路的代价所在
 *
 * `onRevive` 是**每一次重连尝试开始前**都会调的（那一层不知道这一次
 * 接不接得上）。直接在那儿 `onReset`，服务端持续不可用时就会按
 * 1/2/4/8 秒的节奏**反复重拉整个列表**——而那正是这一屏最贵的一次请求，
 * 且每一次都注定拉不到。它不是错，是把一次恢复动作放大成了一串。
 *
 * 所以这里只在 `onRevive` 时**留一个标记**，等这一根真的 `onOpen` 了
 * 才兑现那一次 `onReset`，兑现完立刻清掉。判据是「这一次恢复真的成了」，
 * 不是「我打算恢复」——与退避按「真收到事实」清零是同一条道理。
 *
 * 两种情况**都不调** `onReset`：
 *   首次连接              位置本来就没有，没什么可对齐的
 *   浏览器自己 CONNECTING  它带着 `Last-Event-ID`，位置没丢，重拉是白费
 */
export function useActivityStream(opts: {
  enabled: boolean
  query: ActivityQuery
  onItem: (item: ActivityItem) => void
  onReset: () => void
  onDisconnect: () => void
  onOpen: () => void
}): void {
  const itemRef = useRef(opts.onItem)
  const resetRef = useRef(opts.onReset)
  const disconnectRef = useRef(opts.onDisconnect)
  const openRef = useRef(opts.onOpen)
  const lastCursor = useRef<string | null>(null)
  itemRef.current = opts.onItem
  resetRef.current = opts.onReset
  disconnectRef.current = opts.onDisconnect
  openRef.current = opts.onOpen

  const queryString = activityStreamParams(opts.query).toString()

  useEffect(() => {
    if (!opts.enabled) return
    /**
     * 这一根是我们自己接回来的吗——也就是「位置丢了，连上之后要对齐一次」。
     *
     * 一次重连尝试失败了标记就留着，由**下一次真的连上**那一根兑现，
     * 所以中间试了几次都只兑现一次。首次连接与浏览器自己的 CONNECTING
     * 重连都不会置上它。
     */
    let reviveNeedsAlign = false
    const stream = openRevivingEventSource({
      url: () => {
        const params = new URLSearchParams(queryString)
        if (lastCursor.current !== null) params.set('cursor', lastCursor.current)
        const encoded = params.toString()
        return `${API_BASE}/activity/stream${encoded ? `?${encoded}` : ''}`
      },
      attach: (source, handle) => {
        source.addEventListener('activity', (event: Event) => {
          if (!handle.owns()) return
          // **先记「那边有动静」，再谈这条消息解不解得开。**
          // 一条坏 JSON 也证明连接是活的；把清零挂在解析成功上的话，
          // 一条坏消息会让退避一直涨。
          handle.sawFact()
          const message = event as MessageEvent<string>
          if (message.lastEventId) lastCursor.current = message.lastEventId
          try {
            itemRef.current(JSON.parse(message.data) as ActivityItem)
          } catch {
            resetRef.current()
          }
        })
        source.addEventListener('stream.reset', (event: Event) => {
          if (!handle.owns()) return
          handle.sawFact()
          const message = event as MessageEvent<string>
          if (message.lastEventId) lastCursor.current = message.lastEventId
          resetRef.current()
        })
      },
      onOpen: () => {
        openRef.current()
        // **兑现在这里，不在 onRevive 里。** 那一层每次尝试都会喊一声，
        // 而这一次到底接没接上只有这里知道。
        if (!reviveNeedsAlign) return
        reviveNeedsAlign = false
        resetRef.current()
      },
      onError: () => {
        disconnectRef.current()
      },
      onRevive: () => {
        reviveNeedsAlign = true
      },
    })
    return () => stream.close()
  }, [opts.enabled, queryString])
}
